import { IMAGE_STATE, updateReport, deleteReport } from './ReportStore'
import { uuidv4 } from '../lib/utils/uuid'

// Fields that are local to the outbox record and must not be sent to the API.
const LOCAL_FIELDS = [
  'id', 'imageUri', 'imageType', 'imageUid', 'attemptedCreate', 'serverCreated',
  'imageState', 'attempts', 'imageAttempts', 'lastError', 'storedAt'
]

// Generous cap on photo upload attempts. Photos are retried (never silently
// dropped) up to this many transient failures; beyond it the photo is marked
// DROPPED so the already-created report can finalize instead of retrying forever.
const MAX_IMAGE_ATTEMPTS = 10

// Builds the report payload sent to the create endpoint (keeps uid, drops
// local-only bookkeeping and the inlined image).
function toCreatePayload (record) {
  const payload = { ...record }
  for (const f of LOCAL_FIELDS) { delete payload[f] }
  return payload
}

// Classifies a failure as transient (worth another background sync) or
// permanent (a re-sync will not help). Timeouts (AbortError), offline and
// network errors, 5xx and 429 are transient; other 4xx are permanent.
function isTransientError (err) {
  if (!err) { return false }
  if (err.name === 'AbortError') { return true }
  const status = statusFromError(err)
  if (status === null) { return true } // network/offline/unknown -> transient
  if (status === 429) { return true }
  return status >= 500
}

function statusFromError (err) {
  const m = /status (\d{3})/.exec(err && err.message ? err.message : '')
  return m ? parseInt(m[1], 10) : null
}

// Runs a promise-returning fn with an AbortController-based timeout. fn receives
// the AbortSignal so the underlying fetch can be aborted.
async function withAbortTimeout (timeout, fn) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  try {
    return await fn(controller.signal)
  } finally {
    clearTimeout(timer)
  }
}

// Attempts the create leg for a single record. Advances stage fields and
// persists them. Returns nothing on success. Throws on failure.
async function ensureCreated (deps, db, record) {
  const { reportService, timeouts } = deps

  if (record.serverCreated) { return }

  // Unknown-outcome retry: a previous attempt started but we never confirmed
  // creation. Probe the server with a cheap GET /reports/{uid} before deciding.
  // The idempotent create endpoint safely handles a duplicate uid (it returns
  // the existing report), so re-POSTing is always safe; the GET probe just
  // avoids re-sending the (base64) body when we can confirm the report is
  // already absent-or-present.
  if (record.attemptedCreate) {
    let remote
    try {
      remote = await withAbortTimeout(
        timeouts.createReport,
        (signal) => reportService.get(record.uid, signal)
      )
    } catch (err) {
      // could not determine existence (transient); fall through to create,
      // which is idempotent and will resolve the outcome definitively
      remote = undefined
    }
    // remote === null means confirmed-absent (HTTP 404) -> the earlier attempt
    // never reached the server; proceed to create as normal. A truthy remote
    // means the report exists; the create below is idempotent either way.
    void remote
  }

  record.attemptedCreate = true
  await updateReport(db, record)

  const payload = toCreatePayload(record)
  const result = await withAbortTimeout(
    timeouts.createReport,
    (signal) => reportService.create(payload, signal)
  )

  if (!result || !result.report || !result.report.uid) {
    throw new Error('create report returned no uid')
  }

  record.serverCreated = true
  await updateReport(db, record)
}

// Single in-pass image attempt. The image is a separately-retryable resource:
// the upload is authorized by report ownership (no short-lived token) and made
// idempotent by a stable client imageUid, so a failed photo is retried on a
// later sync rather than dropped. Advances imageState; never fails the report.
// Returns { imageFailedTransiently } so the caller can request a re-sync for a
// still-pending photo without duplicating other reports.
async function tryUploadImage (deps, db, record) {
  const { reportService, timeouts } = deps

  if (record.imageState !== IMAGE_STATE.PENDING) { return { imageFailedTransiently: false } }
  if (!record.imageUri) { return { imageFailedTransiently: false } }

  // Ensure a stable idempotency id even for records stored before imageUid
  // existed, so retries converge to a single image on the backend.
  if (!record.imageUid) {
    record.imageUid = uuidv4()
    await updateReport(db, record)
  }

  const image = { uri: record.imageUri, type: record.imageType }
  try {
    await withAbortTimeout(
      timeouts.uploadReportImage,
      (signal) => reportService.createImage(record.uid, record.imageUid, image, signal)
    )
    record.imageState = IMAGE_STATE.DONE
    await updateReport(db, record)
    return { imageFailedTransiently: false }
  } catch (err) {
    record.lastError = err && err.message ? err.message : String(err)
    const transient = isTransientError(err)
    record.imageAttempts = (record.imageAttempts || 0) + 1

    // A permanent error will never succeed, and a transient error that has
    // exhausted the retry cap is abandoned: mark the photo DROPPED so the
    // already-created report finalizes rather than retrying forever.
    if (!transient || record.imageAttempts >= MAX_IMAGE_ATTEMPTS) {
      record.imageState = IMAGE_STATE.DROPPED
      await updateReport(db, record)
      return { imageFailedTransiently: false }
    }

    // Transient and still within the cap: keep PENDING and request a re-sync so
    // the photo is retried on a later pass rather than dropped.
    await updateReport(db, record)
    return { imageFailedTransiently: true }
  }
}

// Processes one record. Returns { finalized, createFailedTransiently,
// imageFailedTransiently }.
async function syncOneReport (deps, db, record) {
  record.attempts = (record.attempts || 0) + 1

  try {
    await ensureCreated(deps, db, record)
  } catch (err) {
    record.lastError = err && err.message ? err.message : String(err)
    await updateReport(db, record)
    return { finalized: false, createFailedTransiently: isTransientError(err), imageFailedTransiently: false }
  }

  const { imageFailedTransiently } = await tryUploadImage(deps, db, record)

  // Finalize when the report is created and the image is no longer pending.
  if (record.imageState !== IMAGE_STATE.PENDING) {
    await deleteReport(db, record.id)
    return { finalized: true, createFailedTransiently: false, imageFailedTransiently: false }
  }

  // Report is safely on the server; only the photo is still pending. Keep the
  // record queued and request a re-sync when the photo failed transiently, so
  // the photo is eventually uploaded rather than dropped.
  return { finalized: false, createFailedTransiently: false, imageFailedTransiently }
}

// Drains the outbox. Processes every record independently and re-throws (to
// request another background sync) if a still-uncreated report failed
// transiently, or if a still-pending photo failed transiently. One bad photo
// never delays or duplicates other reports, but is retried until it succeeds.
async function syncReports (deps, db, records) {
  let retryNeeded = false
  for (const record of records) {
    try {
      const { createFailedTransiently, imageFailedTransiently } = await syncOneReport(deps, db, record)
      if (createFailedTransiently || imageFailedTransiently) { retryNeeded = true }
    } catch (err) {
      // Unexpected error while persisting/deleting; be conservative and retry.
      retryNeeded = true
    }
  }
  if (retryNeeded) {
    throw new Error('one or more reports still pending; retry sync')
  }
}

export {
  syncReports,
  syncOneReport,
  ensureCreated,
  tryUploadImage,
  toCreatePayload,
  isTransientError,
  withAbortTimeout,
  MAX_IMAGE_ATTEMPTS
}
